/**
 * Lazy loader for the Google Maps JS API (copiado da app maisenergia). A UI
 * pede a chave do tenant em `google-maps-config` (a Function reencaminha para
 * o host, permissão `geo.read`) e injeta a API uma vez por sessão.
 *
 * Idempotent: callers may invoke from multiple components concurrently —
 * the in-flight Promise is shared so we never inject two `<script>` tags.
 *
 * Why not preload in index.html? Two reasons:
 *   1) Some tenants don't have GMaps configured — paying the script
 *      download on every page load is wasteful.
 *   2) The key is tenant-scoped and only known after auth (shell token
 *      exchange).
 */

// We intentionally don't ship `@types/google.maps`. The Map/Marker surface
// we use is tiny and lives behind `any` in DraggableSatelliteMap.tsx.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GoogleNamespace = any

let inflight: Promise<GoogleNamespace> | null = null

// ─── Recusas da chave ────────────────────────────────────────────────────────
// Quando a Google recusa a chave (site não autorizado, faturação desligada…) o
// mapa fica escuro com «For development purposes only» e o motivo só aparece
// na consola. Apanhamo-lo (console.error + gm_authFailure) para o mostrar no
// próprio mapa com o que há a corrigir.

const FIXES: Record<string, string> = {
  RefererNotAllowedMapError: "a chave não autoriza este site. Na Google Cloud → Credenciais → a chave → Restrições de sites, junte https://logistics-optimizer.pages.dev/* e https://*.logistics-optimizer.pages.dev/*.",
  BillingNotEnabledMapError: "a faturação não está ativa no projeto da Google Cloud desta chave (Faturação → ligar uma conta ao projeto).",
  ApiNotActivatedMapError: "a «Maps JavaScript API» não está ativada no projeto da Google Cloud desta chave.",
  ApiTargetBlockedMapError: "a chave tem restrição de APIs e a «Maps JavaScript API» não está na lista.",
  InvalidKeyMapError: "a chave não é válida (confirme-a em Definições → Integrações).",
  ExpiredKeyMapError: "a chave expirou ou foi apagada na Google Cloud.",
  OverQuotaMapError: "a chave passou a quota diária da Google.",
}

let authProblem: string | null = null
const problemListeners = new Set<(problem: string) => void>()

function reportProblem(code: string | null, detail: string | null) {
  if (authProblem) return
  const fix = code ? FIXES[code] : null
  authProblem = `A Google recusou a chave${code ? ` (${code})` : ""}: ${fix ?? "veja o motivo na consola do browser."}${detail ? ` ${detail}` : ""}`
  for (const cb of problemListeners) cb(authProblem)
}

/** Avisa quando a Google recusa a chave; devolve a função para deixar de ouvir. */
export function onGoogleMapsProblem(cb: (problem: string) => void): () => void {
  if (authProblem) cb(authProblem)
  problemListeners.add(cb)
  return () => {
    problemListeners.delete(cb)
  }
}

function watchAuthFailures() {
  const w = window as unknown as { gm_authFailure?: () => void; __kxMapsWatch?: boolean }
  if (w.__kxMapsWatch) return
  w.__kxMapsWatch = true
  let lastCode: string | null = null
  let lastSite: string | null = null
  const original = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    const msg = args.map((a) => (typeof a === "string" ? a : a instanceof Error ? a.message : "")).join(" ")
    const m = /Google Maps JavaScript API error: (\w+)/.exec(msg)
    if (m) {
      lastCode = m[1]
      lastSite = /authorized: (\S+)/.exec(msg)?.[1] ?? null
      // Alguns erros (faturação, API por ativar) não chamam gm_authFailure.
      queueMicrotask(() => reportProblem(lastCode, lastSite ? `Site pedido: ${lastSite}` : null))
    }
    original(...args)
  }
  w.gm_authFailure = () => reportProblem(lastCode, lastSite ? `Site pedido: ${lastSite}` : null)
}

/** True once the dynamic-import loader (`google.maps.importLibrary`) is ready. */
export function isGoogleMapsLoaded(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as unknown as { google?: GoogleNamespace }).google?.maps
      ?.importLibrary === "function"
  )
}

/**
 * Google's official "inline bootstrap loader" for the dynamic library import
 * API (verbatim from the docs, only typed enough to satisfy the compiler).
 *
 * Why this and not a plain `<script src=".../maps/api/js?loading=async">`?
 * That URL only serves a ~13 KB bootstrap that defines `google.maps.Load` and
 * then fetches `main.js` asynchronously — and `importLibrary` lives in
 * `main.js`. A `<script>` `onload` therefore fires BEFORE `importLibrary`
 * exists, so any caller that resolves on `onload` and then calls
 * `importLibrary()` races and intermittently crashes with
 * "importLibrary is not a function".
 *
 * This bootstrap instead defines `google.maps.importLibrary` SYNCHRONOUSLY as
 * a lazy wrapper: it fetches the real API (with the requested libraries) on
 * the first `importLibrary()` call and resolves when ready. No race.
 * Ref: https://developers.google.com/maps/documentation/javascript/load-maps-js-api
 */
function runGoogleMapsBootstrap(config: { key: string; v?: string }): void {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const g = config as Record<string, string>
  const p = "The Google Maps JavaScript API"
  const l = "importLibrary"
  const q = "__ib__"
  const m = document
  const w = window as any
  const b: any = w.google || (w.google = {})
  const d: any = b.maps || (b.maps = {})
  const r = new Set<string>()
  const e = new URLSearchParams()
  let h: Promise<void> | undefined
  const u = () =>
    h ||
    // The `await` on `createElement` defers to a microtask so that all
    // synchronous `importLibrary()` calls in the same tick (e.g. "maps" and
    // "marker") batch their libraries into a single script load.
    // eslint-disable-next-line no-async-promise-executor
    (h = new Promise<void>(async (f, n) => {
      const a = await m.createElement("script")
      e.set("libraries", [...r] + "")
      for (const k in g)
        e.set(k.replace(/[A-Z]/g, (t) => "_" + t[0].toLowerCase()), g[k])
      e.set("callback", "google.maps." + q)
      a.src = "https://maps.googleapis.com/maps/api/js?" + e
      d[q] = f
      a.onerror = () => {
        h = undefined
        n(new Error(p + " could not load."))
      }
      a.nonce =
        (m.querySelector("script[nonce]") as HTMLScriptElement | null)?.nonce || ""
      m.head.append(a)
    }))
  if (d[l]) {
    console.warn(p + " only loads once. Ignoring:", g)
  } else {
    // After `main.js` runs it overwrites `d[l]` with the real importLibrary,
    // so the `.then` below invokes the real one (no recursion).
    d[l] = (x: string, ...n: unknown[]) => (r.add(x), u().then(() => d[l](x, ...n)))
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export function loadGoogleMaps(apiKey: string): Promise<GoogleNamespace> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Maps requer ambiente browser"))
  }
  const win = window as unknown as { google?: GoogleNamespace }
  if (typeof win.google?.maps?.importLibrary === "function") {
    return Promise.resolve(win.google)
  }
  if (inflight) return inflight

  inflight = new Promise<GoogleNamespace>((resolve, reject) => {
    try {
      watchAuthFailures()
      // `v=quarterly` keeps the API on the stable channel without auto-jumping
      // to weekly updates that occasionally ship breaking changes.
      runGoogleMapsBootstrap({ key: apiKey, v: "quarterly" })
      if (typeof win.google?.maps?.importLibrary === "function") {
        resolve(win.google)
      } else {
        // Reset so a later retry can re-run the bootstrap.
        inflight = null
        reject(new Error("Google Maps: bootstrap não expôs importLibrary"))
      }
    } catch (err) {
      inflight = null
      reject(err instanceof Error ? err : new Error("Falha a inicializar Google Maps"))
    }
  })
  return inflight
}
